import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { BookingStatusBadge, Card, ErrorNote, Spinner } from "@/components/ui";
import { LevelBadge } from "@/pages/LeadsPage";
import type { LeadDetail } from "@/lib/types";
import { tStatus } from "@/i18n";
import { formatCompanyDate, formatCompanyDateTime } from "@/lib/time";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-sand-muted">{label}</dt>
      <dd className="mt-0.5 text-sm">{value ?? "—"}</dd>
    </div>
  );
}

export default function LeadDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();

  const q = useQuery({
    queryKey: ["lead", id],
    queryFn: () => api.get<LeadDetail>(`/leads/${id}`),
  });

  if (q.isLoading) return <Spinner />;
  if (q.isError) {
    const err = q.error;
    return (
      <ErrorNote
        message={
          err instanceof ApiError && err.status === 404
            ? t("leads.notFound")
            : (err as Error).message
        }
      />
    );
  }

  const lead = q.data!;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/leads" className="text-sm text-sand-muted hover:text-gold">
          {t("leads.back")}
        </Link>
        <h1 className="text-lg font-semibold">{tStatus(lead.category)}</h1>
        <LevelBadge level={lead.level} />
      </div>

      <Card>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field
            label={t("leads.observedAt")}
            value={formatCompanyDateTime(lead.observed_at)}
          />
          <Field label={t("leads.sender")} value={lead.sender} />
          <Field label={t("leads.source")} value={tStatus(lead.source)} />
          <Field label={t("leads.sourceChat")} value={lead.source_chat} />
          <Field label={t("leads.score")} value={lead.score?.toString()} />
          <Field
            label={t("leads.matchedKeywords")}
            value={lead.matched_keywords.join(", ") || "—"}
          />
        </dl>
        {lead.message_link && (
          <div className="mt-4">
            <a
              className="text-sm text-gold hover:underline"
              href={lead.message_link}
              target="_blank"
              rel="noreferrer"
            >
              {t("leads.openInTelegram")}
            </a>
          </div>
        )}
        <div className="mt-4">
          <dt className="text-xs text-sand-muted">
            {t("leads.message")}
          </dt>
          <dd className="mt-1 whitespace-pre-wrap text-sm">{lead.message_text}</dd>
        </div>
      </Card>

      <div>
        <h2 className="mb-2 text-sm font-semibold text-sand">
          {t("leads.converted")}
        </h2>
        {lead.converted_bookings.length === 0 ? (
          <p className="text-sm text-sand-muted">{t("leads.notConverted")}</p>
        ) : (
          <Card>
            <ul className="divide-y divide-olive-700 text-sm">
              {lead.converted_bookings.map((b) => (
                <li key={b.id} className="flex items-center gap-3 py-1.5">
                  <Link
                    className="font-medium text-sand hover:text-gold"
                    to={`/bookings/${b.booking_number}`}
                  >
                    {b.booking_number}
                  </Link>
                  <BookingStatusBadge booking={b} />
                  <span className="ml-auto text-xs text-sand-muted">
                    {formatCompanyDate(b.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
